import React, { ReactNode, useCallback, CSSProperties, useRef, useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ExtendedRefs,
  FloatingContext,
  FloatingFocusManager,
  FloatingPortal,
  useTransitionStyles,
} from '@floating-ui/react';
import { useFocusRing } from '@react-aria/focus';
import classNames from 'classnames';
import { getKeyFromItem } from '../../utils/shared';
import { getZIndex } from '../../styles';
import { FLOATING_ROOT_ID } from '../../constants';
import { getInputHeightBySize, getInputSpacingBySize } from '../input/styles';
import { AriaFocusProps } from '../../types/common';
import { dropdown_item_group_css, dropdown_item_group_focus_style } from '../dropdown/styles';
import { DropdownItem } from '../dropdown/DropdownItem';
import { DropdownInputOption, DropdownOption } from './types';
import { semantic_colors } from '../../foundation';
import styled from '@emotion/styled';
import { DEFAULT_DROPDOWN_MAX_HEIGHT } from './constants';
import { InputSize } from '../input/types';
import { DropdownInternalOption } from './DropdownInternalOptions';

export interface DropdownOptionsProps<OptionValue> {
  className?: string;
  /** @default getZIndex('dropdown') */
  zIndex?: number;
  noDataText?: ReactNode;
  initialFocus?: number;
  /** @default 'medium' */
  size?: InputSize;
  optionItemHeight?: number;
  options: Array<DropdownOption<OptionValue> | DropdownInputOption<OptionValue>>;
  placeholder?: string;
  value?: OptionValue;
  onClose: () => void;
  keyExtractor?: (item: DropdownOption<OptionValue>, index: number) => string | number;
  onChange?: (value: OptionValue) => void;
  renderLabel?: (selectedOption: DropdownOption<OptionValue>) => ReactNode;
  focusIndex?: number | null;
  getFloatingProps: (props: Record<string, unknown>) => Record<string, unknown>;
  getItemProps: (props: Record<string, unknown>) => Record<string, unknown>;
  context: FloatingContext;
  floatingStyles: CSSProperties;
  refs: ExtendedRefs<Element>;
  setRefs: (node: HTMLElement | null, index: number) => void;
  isPositioned?: boolean;
  setFocusIndex?: (index: number) => void;
  enableVirtualScroll?: boolean;
}

export const DropdownOptions = <OptionValue extends string | number>({
  className,
  zIndex = getZIndex('dropdown'),
  size: sizeProp = 'medium',
  optionItemHeight,
  initialFocus,
  options,
  onClose,
  noDataText,
  value: valueProp,
  unstableFocusVisible,
  keyExtractor = getKeyFromItem,
  onChange,
  focusIndex,
  context,
  getFloatingProps,
  getItemProps,
  floatingStyles,
  refs,
  setRefs,
  isPositioned,
  setFocusIndex,
  enableVirtualScroll,
}: DropdownOptionsProps<OptionValue> & AriaFocusProps) => {
  const { isFocusVisible, focusProps } = useFocusRing({ within: true, isTextInput: true });
  const selectedIndex = options.findIndex(({ value }) => value === valueProp);
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: 100,
    initial: {
      opacity: 0,
    },
    common: {
      transitionTimingFunction: 'linear',
    },
  });

  const virtualParentRef = useRef();
  const rowVirtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => virtualParentRef.current ?? null,
    estimateSize: () => (optionItemHeight ? optionItemHeight : getInputHeightBySize(sizeProp)),
    overscan: 5,
    enabled: isPositioned && enableVirtualScroll,
  });

  useLayoutEffect(() => {
    if (isPositioned && enableVirtualScroll) {
      if (focusIndex === null && selectedIndex === null) {
        rowVirtualizer.scrollToIndex(0, {
          behavior: 'auto',
        });
      }
      if (focusIndex !== null) {
        // @ts-expect-error : TS2345
        rowVirtualizer.scrollToIndex(focusIndex, {
          behavior: 'auto',
        });
      }
    }
  }, [rowVirtualizer, isPositioned, focusIndex, selectedIndex, refs]);

  const handleChange = useCallback(
    (value: OptionValue) => {
      onChange?.(value);
      onClose();
    },
    [onChange],
  );

  const internalOptionProps = {
    setRefs,
    selectedIndex,
    focusIndex,
    size: sizeProp,
    optionItemHeight,
    onChange: handleChange,
    keyExtractor,
    getItemProps,
  };

  return (
    <>
      {isMounted && (
        <FloatingPortal id={FLOATING_ROOT_ID}>
          <FloatingFocusManager context={context} initialFocus={initialFocus} returnFocus={false}>
            <ul
              {...getFloatingProps({
                ref: refs.setFloating,
                className: classNames('pds-dropdown', className),
                style: {
                  ...floatingStyles,
                  zIndex,
                  outline: 'none',
                  ...styles,
                },
                onKeyDown(event: KeyboardEvent) {
                  if (event.key === 'Tab') {
                    onClose();
                  }
                  // @ts-expect-error : TS18048
                  if (event.key === 'ArrowUp' && (focusIndex === null || focusIndex < 0)) {
                    setFocusIndex?.(options.length - 1);
                  }
                },
              })}
              css={[
                dropdown_item_group_css,
                { padding: getInputSpacingBySize(sizeProp) },
                { ...((unstableFocusVisible ?? isFocusVisible) && dropdown_item_group_focus_style) },
              ]}
              {...focusProps}
            >
              {/* @ts-expect-error: TS2322  */}
              <Scrollable ref={virtualParentRef}>
                {options.length > 0 ? (
                  enableVirtualScroll ? (
                    <VirtualContainer totalSize={rowVirtualizer.getTotalSize()}>
                      {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                        const index = virtualItem.index;
                        const option = { ...options[index], start: virtualItem.start };
                        return (
                          <DropdownInternalOption key={index} option={option} index={index} {...internalOptionProps} />
                        );
                      })}
                    </VirtualContainer>
                  ) : (
                    options.map((option, index) => {
                      return (
                        <DropdownInternalOption key={index} option={option} index={index} {...internalOptionProps} />
                      );
                    })
                  )
                ) : noDataText ? (
                  <NoDataItem size={sizeProp}>{noDataText}</NoDataItem>
                ) : (
                  <></>
                )}
              </Scrollable>
            </ul>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

const Scrollable = styled.div`
  width: 100%;
  max-height: ${DEFAULT_DROPDOWN_MAX_HEIGHT}px;
  overflow: auto;
`;

const VirtualContainer = styled.div<{ totalSize: number }>`
  width: 100%;
  position: relative;
  ${({ totalSize }) => `height: ${totalSize}px`};
`;

const NoDataItem = styled(DropdownItem)`
  color: ${semantic_colors.content.tertiary};
  cursor: default;
  &:hover,
  &:focus,
  &:active {
    background-color: transparent;
  }
`;
